/**
 * hoy-feed.ts
 * Pure selection logic behind the Hoy screen (HomeView.vue): which of the
 * worker's open failures, pending trips, schedule and absences deserve a card
 * today. Kept free of Vue and of the API so every rule is unit-tested
 * directly (lib/__tests__/hoy-feed.spec.ts).
 *
 * operator-app-unificada T5 — featuredRequest ("Tus solicitudes") replaces
 * the former nextAbsence ("Próximo").
 *
 * Dates are calendar dates (yyyy-mm-dd) in the phone's local time zone:
 * "today" for a worker in Neuquén is their local day, not the UTC one.
 */
import type { FleetFailureResponse, Importance, VehicleTripResponse } from '@suite/contracts-servicios';
import type { SelfCalendarDayResponse, SelfCalendarEventResponse } from '@suite/contracts-rrhh';

export const DIAGRAM_DAYS = 14;
export const MAX_FAILURE_CARDS = 3;

const IMPORTANCE_RANK: Record<Importance, number> = { urgent: 3, high: 2, normal: 1, low: 0 };

export function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return localIsoDate(new Date(y, m - 1, d + days));
}

/** "jueves 9" — weekday and day of month, the way a rotation is talked about. */
export function formatWeekdayDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const weekday = new Date(y, m - 1, d).toLocaleDateString('es-AR', { weekday: 'long' });
  return `${weekday} ${d}`;
}

/** "07:42", or "jueves 30 · 07:42" when it was not on `today` (a dictation or form kept on the phone). */
export function recordedLabel(iso: string, today: string): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return localIsoDate(at) === today ? time : `${formatWeekdayDay(localIsoDate(at))} · ${time}`;
}

/** "31/10" */
export function formatDayMonth(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function greetingFor(hour: number): string {
  if (hour < 13) return 'Buen día';
  if (hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

function failureTime(f: FleetFailureResponse): number {
  return new Date(f.reportedAt ?? f.createdAt).getTime();
}

/** Unresolved failures, most important first, then most recent. */
export function openFailures(items: readonly FleetFailureResponse[], limit = MAX_FAILURE_CARDS): FleetFailureResponse[] {
  return items
    .filter((f) => f.currentState !== 'RESUELTO')
    .sort((a, b) => IMPORTANCE_RANK[b.importance] - IMPORTANCE_RANK[a.importance] || failureTime(b) - failureTime(a))
    .slice(0, limit);
}

/** Trips the worker asked for that nobody approved or rejected yet. */
export function pendingTrips(items: readonly VehicleTripResponse[]): VehicleTripResponse[] {
  return items.filter((t) => t.currentState === 'SOLICITADO');
}

export type DiagramDayType = SelfCalendarDayResponse['dayType'];

export interface DiagramStripDay {
  readonly date: string;
  readonly dayType: DiagramDayType | null;
  readonly isToday: boolean;
}

export interface DiagramSummary {
  readonly today: SelfCalendarDayResponse | null;
  /** First day in the window whose type differs from today's. */
  readonly nextChange: { readonly date: string; readonly dayType: DiagramDayType } | null;
  readonly strip: readonly DiagramStripDay[];
  /** "día 10 de 14": today's place in its run of same-type days. Null when the window cuts the run. */
  readonly stretch: { readonly day: number; readonly length: number } | null;
  /** "14×14": work days × rest days. Null unless both runs fit whole in the window. */
  readonly rotation: string | null;
  /** On a work day, the day the worker is back after the coming rest. */
  readonly returnDate: string | null;
}

/**
 * Days the Hoy calendar request covers around today: enough to see a whole
 * 14×14 (or 21×7) rotation on either side, so "día X de N" and the return
 * date can be told without guessing.
 */
export const DIAGRAM_LOOKBACK_DAYS = 42;
export const DIAGRAM_LOOKAHEAD_DAYS = 63;

interface Run {
  readonly start: string;
  readonly end: string;
  readonly whole: boolean;
}

/** The run of days sharing `from`'s type, walking both ways inside [window.from, window.to]. */
function runAround(
  byDate: ReadonlyMap<string, SelfCalendarDayResponse>,
  date: string,
  window: { readonly from: string; readonly to: string },
): Run | null {
  const type = byDate.get(date)?.dayType;
  if (!type) return null;
  let start = date;
  while (byDate.get(addDays(start, -1))?.dayType === type) start = addDays(start, -1);
  let end = date;
  while (byDate.get(addDays(end, 1))?.dayType === type) end = addDays(end, 1);
  // A run is whole only when a different planned day closes it on both sides inside the window.
  const closedBefore = addDays(start, -1) >= window.from && byDate.get(addDays(start, -1)) !== undefined;
  const closedAfter = addDays(end, 1) <= window.to && byDate.get(addDays(end, 1)) !== undefined;
  return { start, end, whole: closedBefore && closedAfter };
}

function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** Null when the worker has no planned schedule in the window at all. */
export function diagramSummary(
  days: readonly SelfCalendarDayResponse[],
  today: string,
  window: { readonly from: string; readonly to: string } = { from: today, to: addDays(today, DIAGRAM_DAYS - 1) },
): DiagramSummary | null {
  if (days.length === 0) return null;
  const byDate = new Map(days.map((d) => [d.date, d]));
  const strip: DiagramStripDay[] = [];
  for (let i = 0; i < DIAGRAM_DAYS; i += 1) {
    const date = addDays(today, i);
    strip.push({ date, dayType: byDate.get(date)?.dayType ?? null, isToday: i === 0 });
  }
  const todayDay = byDate.get(today) ?? null;
  let nextChange: DiagramSummary['nextChange'] = null;
  if (todayDay) {
    const change = strip.find((d) => d.dayType !== null && d.dayType !== todayDay.dayType);
    if (change?.dayType) nextChange = { date: change.date, dayType: change.dayType };
  }

  const current = todayDay ? runAround(byDate, today, window) : null;
  const stretch = current?.whole
    ? { day: daysBetween(current.start, today) + 1, length: daysBetween(current.start, current.end) + 1 }
    : null;
  const following = current?.whole ? runAround(byDate, addDays(current.end, 1), window) : null;
  // The other half of the rotation: the next run, or the previous one when the next is cut by the window.
  const previous = current?.whole ? runAround(byDate, addDays(current.start, -1), window) : null;
  const other = following?.whole ? following : previous?.whole ? previous : null;
  const otherLength = other ? daysBetween(other.start, other.end) + 1 : null;
  let rotation: string | null = null;
  if (stretch && otherLength !== null) {
    rotation = todayDay?.dayType === 'labor' ? `${stretch.length}×${otherLength}` : `${otherLength}×${stretch.length}`;
  }
  const returnDate = todayDay?.dayType === 'labor' && following?.whole ? addDays(following.end, 1) : null;

  return { today: todayDay, nextChange, strip, stretch, rotation, returnDate };
}

/**
 * "Tus solicitudes" on Inicio (operator-app-unificada T5): the one request
 * worth showing — the one waiting for approval first (earliest), else the
 * next approved absence still running or ahead, else the most recent approved one.
 */
export function featuredRequest(events: readonly SelfCalendarEventResponse[], today: string): SelfCalendarEventResponse | null {
  const byStart = (a: SelfCalendarEventResponse, b: SelfCalendarEventResponse) => a.startDate.localeCompare(b.startDate);
  const pending = events.filter((e) => e.currentState === 'SOLICITADA').sort(byStart);
  if (pending[0]) return pending[0];
  const approved = events.filter((e) => e.currentState === 'APROBADA').sort(byStart);
  return approved.find((e) => e.endDate >= today) ?? approved.at(-1) ?? null;
}
